/** @jsxImportSource @emotion/react */
import { ButtonProps } from '@mui/material';
import MuiTooltip from '@mui/material/Tooltip';
import React from 'react';

import { badgeCss } from 'src/common/ui/components/Badge/Badge.css';
import { BoulderIcon } from 'src/common/ui/components/BoulderIcon/BoulderIcon';
import { type BoulderIconName } from 'src/common/ui/components/icons';

export type BadgeProps = {
  color?: ButtonProps['color'];
  icon?: BoulderIconName;
  label?: string;
  link?: string;
  onClick?: () => void;
  rounded?: boolean;
  tooltip?: React.ReactElement | string;
};

/**
 * Standard badges for displaying short snippets of information
 * and / or linking to related sections of the site.
 *
 * @param color - The (intent-based) color of the badge (e.g. primary, error, warning).
 * @param icon - The standard name of an icon to display in the badge.
 * @param label - The text to display in the badge.
 * @param link - The URL to navigate to when the badge is clicked. If provided the badge
 *               will be rendered as an anchor tag.
 * @param onClick - The function to call when the badge is clicked. If provided the badge
 *                  will be rendered as a button.
 * @param rounded - Whether the badge should have extra-rounded corners.
 * @param tooltip - The content of the tooltip to display when hovering over the badge.
 *                  Provide a string for simple tooltips or a React fragment for more complex
 *                  ones.
 */
export const Badge = (props: BadgeProps) => {
  const classNameList: string[] = [];
  let Element: React.ElementType = 'div';

  if (props.color) {
    classNameList.push(props.color);
  }

  if (props.rounded) {
    classNameList.push('rounded');
  }

  if (props.onClick || props.link) {
    classNameList.push('interactive');
  }

  if (props.onClick) {
    Element = 'button';
  }

  if (props.link) {
    Element = 'a';
  }

  return (
    <MuiTooltip title={props.tooltip} enterDelay={300} enterNextDelay={300} leaveTouchDelay={300}>
      <Element
        href={props.link}
        className={classNameList.join(' ')}
        onClick={props.onClick}
        css={badgeCss}
      >
        {props.icon ? <BoulderIcon icon={props.icon} size={18} /> : null}
        {props.label}
      </Element>
    </MuiTooltip>
  );
};
